/**
 * Qualitäts-Dashboard für PDL-Suite-Kunden (und Admin).
 *
 * Drei Bereiche:
 *   1. Halluzinationsschutz · Maßnahmenpläne (vorhandener Endpoint /pflege-ki/halluz-stats)
 *   2. NBA-Konsistenz · Höherstufungs-/Widerspruchs-Briefe (neuer Endpoint /pflege-ki/nba/konsistenz-stats)
 *   3. NBA-Gutachten-Qualität · Begründungsstärke-Verteilung + Avg-Werte
 *
 * Reine Read-only-Visualisierung. CSV-Export für Konsistenz-Audit eingebaut.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, AlertTriangle, ShieldAlert, CheckCircle2, BarChart3,
  Download, Stethoscope, ListChecks, Sparkles, Clock, Target, Award,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PkiMonatsreport from "../../components/pflege-ki/PkiMonatsreport";
import PkiMailDispatchButton from "../../components/pflege-ki/PkiMailDispatchButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIOD_OPTIONS = [
  { value: 7,   label: "7 Tage"  },
  { value: 30,  label: "30 Tage" },
  { value: 90,  label: "90 Tage" },
  { value: 180, label: "6 Monate" },
];

export default function AdminQualitaetsDashboard() {
  const [days, setDays] = useState(30);
  const [halluz, setHalluz] = useState(null);
  const [konsistenz, setKonsistenz] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [halluzError, setHalluzError] = useState(null);
  const [konsistenzError, setKonsistenzError] = useState(null);
  const [kpisError, setKpisError] = useState(null);
  const [briefingError, setBriefingError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      setHalluzError(null);
      setKonsistenzError(null);
      setKpisError(null);
      setBriefingError(null);
      const [h, k, kpi, wb] = await Promise.allSettled([
        axios.get(`${API}/pflege-ki/halluz-stats?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/pflege-ki/nba/konsistenz-stats?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/pflege-ki/halluz-kpis?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/pflege-ki/wochenbriefing?days=7`, { withCredentials: true }),
      ]);
      if (cancelled) return;
      if (h.status === "fulfilled") setHalluz(h.value.data);
      else setHalluzError(h.reason?.response?.data?.detail || "Halluz-Stats nicht verfügbar.");
      if (k.status === "fulfilled") setKonsistenz(k.value.data);
      else setKonsistenzError(k.reason?.response?.data?.detail || "Konsistenz-Stats nicht verfügbar.");
      if (kpi.status === "fulfilled") setKpis(kpi.value.data);
      else setKpisError(kpi.reason?.response?.data?.detail || "Halluz-KPIs nicht verfügbar.");
      if (wb.status === "fulfilled") setBriefing(wb.value.data);
      else setBriefingError(wb.reason?.response?.data?.detail || "Wochenbriefing nicht verfügbar.");
      setLoading(false);
    };
    loadData();
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="space-y-10" data-testid="qualitaets-dashboard">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-1.5">
            PDL Suite · Qualitäts-Dashboard
          </div>
          <h1 className="font-display text-3xl text-sand-800 font-semibold tracking-tight leading-tight">
            Halluzinationsschutz &amp; NBA-Konsistenz
          </h1>
          <p className="mt-2 text-sand-600 text-[13.5px] max-w-3xl leading-relaxed">
            Aggregierte Sicht auf die deterministischen Schutzmechanismen der Pflege-KI.
            Zeigt, wie oft die KI fachlich diszipliniert war — und wo Begründungslücken aufgetaucht sind.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-clay-dark mr-1">Zeitraum</span>
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.value}
              onClick={() => setDays(p.value)}
              data-testid={`period-${p.value}`}
              className={`h-9 px-3 rounded-full text-[12px] font-semibold transition-colors ${
                days === p.value
                  ? "bg-sand-800 text-cream-50"
                  : "bg-cream-100 text-sand-700 hover:bg-cream-200"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      {loading && <SkeletonGrid />}

      {/* M · MONTAGSBRIEFING · letzte 7 Tage */}
      {!loading && (
        <Section
          title="M · Montagsbriefing · letzte 7 Tage"
          icon={Stethoscope}
          subtitle="Strukturierter Qualitätsstatus, Top-Feststellungen und konkrete Wochenmaßnahmen — Ihr wöchentliches Führungsinstrument."
          error={briefingError}
          actions={<PkiMailDispatchButton variant="wochenbriefing" testidPrefix="wochenbriefing" />}
        >
          {briefing && <BriefingPanel data={briefing} />}
        </Section>
      )}

      {/* N · PDF-MONATSREPORT · 4 Wochen */}
      {!loading && (
        <Section
          title="N · PDF-Monatsreport · 4 Wochen"
          icon={FileText}
          subtitle="Executive Management Report mit 4-Wochen-Sparklines, Top-3-Risiken, Top-3-Verbesserungen, Coaching-Plan und Ampel-Fazit — direkt druckbar als PDF."
          actions={<PkiMailDispatchButton variant="monatsreport" weeks={4} testidPrefix="monatsreport" />}
        >
          <PkiMonatsreport weeks={4} />
        </Section>
      )}

      {/* 0 · HERO-KPIs · Verhinderte Halluzinationen */}
      {!loading && (
        <Section
          title="0 · Verhinderte Halluzinationen"
          icon={Sparkles}
          subtitle="Was die deterministischen Schutzregeln aktiv aufgedeckt haben — die Story für MD-Prüfungen und Tarifgespräche."
          error={kpisError}
        >
          {kpis && <HeroKpisPanel data={kpis} />}
        </Section>
      )}

      {/* 1 · Halluzinationsschutz · Maßnahmenpläne */}
      {!loading && (
        <Section
          title="1 · Halluzinationsschutz · Maßnahmenpläne"
          icon={Shield}
          subtitle="Wie oft erkannte der deterministische Halluz-Schutz Widersprüche, Spekulationen oder fehlende Begründungen?"
          error={halluzError}
        >
          {halluz && <HalluzPanel data={halluz} />}
        </Section>
      )}

      {/* 2 · NBA-Konsistenz */}
      {!loading && (
        <Section
          title="2 · NBA-Konsistenz · Höherstufung / Widerspruch"
          icon={ShieldAlert}
          subtitle="Wie oft hat das System Briefe blockiert, eine Pflicht-Begründung gefordert oder erlaubt — sortiert nach NBA-Begründungsstärke."
          error={konsistenzError}
        >
          {konsistenz && <KonsistenzPanel data={konsistenz} />}
        </Section>
      )}

      {/* 3 · NBA-Gutachten-Qualität */}
      {!loading && konsistenz && (
        <Section
          title="3 · NBA-Gutachten-Qualität"
          icon={Stethoscope}
          subtitle="Begründungsstärke-Verteilung und Durchschnittswerte aus den erzeugten Gutachten."
        >
          <GutachtenPanel data={konsistenz} />
        </Section>
      )}
    </div>
  );
}

/* ---------- Sub-Komponenten ---------- */

function Section({ title, icon: Icon, subtitle, children, error, actions }) {
  return (
    <section className="rounded-2xl border border-cream-300 bg-white p-6 md:p-8" data-testid={`section-${title.split(" ")[0]}`}>
      <div className="flex items-start gap-3 mb-5 pb-5 border-b border-cream-200">
        <div className="rounded-xl bg-terra/12 text-terra h-11 w-11 flex items-center justify-center shrink-0">
          {Icon && <Icon className="h-5 w-5" strokeWidth={1.7} />}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl text-sand-800 font-semibold leading-tight">{title}</h2>
          {subtitle && <p className="mt-1 text-[13px] text-sand-600 leading-relaxed">{subtitle}</p>}
        </div>
        {actions && <div className="shrink-0 ml-2">{actions}</div>}
      </div>
      {error ? (
        <div className="rounded-xl border border-saffron/45 bg-saffron/12 p-4 text-[13px] text-clay-dark italic">
          {error}
        </div>
      ) : (
        children
      )}
    </section>
  );
}

function HalluzPanel({ data }) {
  const total = data.total_drafts || 0;
  const sv = data.severity_verteilung || { gruen: 0, gelb: 0, rot: 0 };
  return (
    <div className="space-y-6" data-testid="halluz-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Maßnahmenpläne" value={total} testid="kpi-halluz-total" />
        <Kpi label="Mit Hinweisen" value={data.mit_hinweisen || 0} testid="kpi-halluz-hinweise" />
        <Kpi label="Kritisch (rot)" value={data.kritisch_rot || 0} accent="brand-darkred" testid="kpi-halluz-rot" />
        <Kpi label="Freigaben mit Override" value={data.freigegeben_trotz_rot || 0} accent="saffron" testid="kpi-halluz-override" />
      </div>

      {/* Severity Donut (als CSS-Conic) */}
      <div className="grid md:grid-cols-2 gap-6">
        <DonutCard
          title="Severity-Verteilung"
          testid="halluz-donut"
          segments={[
            { label: "Grün",  value: sv.gruen, color: "var(--sage-dark, #6f8c6f)" },
            { label: "Gelb",  value: sv.gelb,  color: "var(--saffron, #d99d3a)"   },
            { label: "Rot",   value: sv.rot,   color: "var(--brand-darkred, #b04545)" },
          ]}
        />
        <div data-testid="halluz-top-regeln" className="rounded-xl border border-cream-300 bg-cream-50/50 p-5">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-clay-dark mb-3 flex items-center gap-1.5">
            <ListChecks className="h-3.5 w-3.5" />
            Top-Regeln (geblockt / erkannt)
          </div>
          {(data.top_regeln || []).length === 0 ? (
            <p className="text-[13px] text-sand-600 italic">Keine Regel hat im Zeitraum gegriffen.</p>
          ) : (
            <ol className="space-y-2">
              {data.top_regeln.map((r, i) => (
                <li key={r.regel} className="flex items-center justify-between gap-2 text-[13px]">
                  <span className="text-sand-800 truncate">
                    <span className="text-sand-500 mr-2">{i + 1}.</span>
                    {r.regel}
                  </span>
                  <span className={`font-mono text-[12.5px] font-bold ${
                    r.severity === "rot" ? "text-brand-darkred"
                    : r.severity === "gelb" ? "text-saffron-dark"
                    : "text-sage-dark"
                  }`}>
                    {r.anzahl}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {data.label && (
        <p className="text-[12.5px] text-sand-600 italic" data-testid="halluz-label">{data.label}</p>
      )}
    </div>
  );
}

function KonsistenzPanel({ data }) {
  const k = data.konsistenz || { by_status: {}, by_outcome: {}, total: 0 };
  const sBy = k.by_status || {};
  return (
    <div className="space-y-5" data-testid="konsistenz-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Gesamt Checks"
             value={k.total}
             testid="kpi-k-total" />
        <Kpi label="Blockiert · kein Gutachten"
             value={sBy.block_no_gutachten || 0}
             accent="brand-darkred"
             testid="kpi-k-block-nog" />
        <Kpi label="Blockiert · eingeschränkt"
             value={sBy.block_eingeschraenkt || 0}
             accent="brand-darkred"
             testid="kpi-k-block-ein" />
        <Kpi label="Warn · schwach (mit Override)"
             value={sBy.warn_schwach || 0}
             accent="saffron"
             testid="kpi-k-warn" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="OK · mittel"
             value={sBy.ok_mittel || 0}
             accent="sage-dark"
             testid="kpi-k-ok-mittel" />
        <Kpi label="OK · stark"
             value={sBy.ok_stark || 0}
             accent="sage-dark"
             testid="kpi-k-ok-stark" />
        <Kpi label="User wählte Leistungsanpassung"
             value={(k.by_outcome || {}).user_alternative || 0}
             accent="terra"
             testid="kpi-k-alt" />
      </div>

      <Hint>
        <strong>So wirkt der Schutz:</strong> Jede blockierte oder mit Override gewarnte Höherstufung
        ist ein potenzieller MD-Konflikt, der vor der Versendung verhindert wurde.
        Die fachliche Konsistenz zwischen NBA-Gutachten und Höherstufungsbrief
        ist Eure stärkste Verteidigungslinie gegen Haftungsrisiken.
      </Hint>
    </div>
  );
}

function GutachtenPanel({ data }) {
  const g = data.gutachten || {};
  const bs = g.begruendungsstaerke_verteilung || {};
  return (
    <div className="space-y-5" data-testid="gutachten-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Gutachten gesamt" value={g.total || 0} testid="kpi-g-total" />
        <Kpi label="⌀ fehlende Infos pro Gutachten" value={g.avg_fehlende_informationen ?? "–"} testid="kpi-g-fehl" />
        <Kpi label="⌀ blockierte Empfehlungen" value={g.avg_blockierte_empfehlungen ?? "–"} accent="terra" testid="kpi-g-block" />
        <Kpi label="Stark + Mittel" value={(bs.stark || 0) + (bs.mittel || 0)} accent="sage-dark" testid="kpi-g-mittel-stark" />
      </div>

      <div className="rounded-xl border border-cream-300 bg-cream-50/50 p-5" data-testid="gutachten-bs-verteilung">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-clay-dark mb-3">
          Begründungsstärke-Verteilung
        </div>
        <div className="space-y-2">
          {["stark", "mittel", "schwach", "eingeschränkt"].map((k) => {
            const n = bs[k] || 0;
            const total = g.total || 1;
            const pct = Math.round((n / total) * 100);
            const color =
              k === "stark"   ? "bg-sage-dark" :
              k === "mittel"  ? "bg-saffron"   :
              k === "schwach" ? "bg-saffron-dark" :
              "bg-brand-darkred";
            return (
              <div key={k} data-testid={`bs-bar-${k}`}>
                <div className="flex items-center justify-between text-[12px] text-sand-700 mb-0.5">
                  <span className="uppercase tracking-[0.18em] font-bold">{k}</span>
                  <span className="font-mono">{n} · {pct}%</span>
                </div>
                <div className="h-2 bg-cream-200 rounded-full overflow-hidden">
                  <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  const accentMap = {
    "brand-darkred": "text-brand-darkred border-brand-darkred/30 bg-brand-darkred/8",
    "saffron":       "text-clay-dark border-saffron/45 bg-saffron/12",
    "sage-dark":     "text-sage-dark border-sage-dark/35 bg-sage-dark/10",
    "terra":         "text-terra border-terra/35 bg-terra/8",
  };
  const cls = accent ? accentMap[accent] : "text-sand-800 border-cream-300 bg-cream-50/40";
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${cls}`}>
      <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold opacity-80">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold leading-none">{value}</div>
    </div>
  );
}

function DonutCard({ title, segments, testid }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  // CSS-Conic gradient für den Donut
  let acc = 0;
  const stops = total > 0 ? segments.map((s) => {
    const from = (acc / total) * 360;
    acc += s.value || 0;
    const to = (acc / total) * 360;
    return `${s.color} ${from}deg ${to}deg`;
  }).join(", ") : "var(--cream-300, #e7dccc) 0deg 360deg";
  return (
    <div data-testid={testid} className="rounded-xl border border-cream-300 bg-cream-50/50 p-5 flex flex-col md:flex-row items-center md:items-start gap-5">
      <div
        className="h-32 w-32 rounded-full shrink-0 relative"
        style={{ background: `conic-gradient(${stops})` }}
        aria-label="Donut-Diagramm"
      >
        <div className="absolute inset-3 rounded-full bg-cream-50/95 flex items-center justify-center">
          <div className="text-center">
            <div className="font-display text-xl font-bold text-sand-800 leading-none">{total}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-sand-500 mt-1">gesamt</div>
          </div>
        </div>
      </div>
      <div className="flex-1 w-full">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-clay-dark mb-2">{title}</div>
        <ul className="space-y-1.5">
          {segments.map((s) => (
            <li key={s.label} className="flex items-center justify-between text-[13px]">
              <span className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                {s.label}
              </span>
              <span className="font-mono text-sand-700">{s.value} · {total > 0 ? Math.round(s.value / total * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Hint({ children }) {
  return (
    <div className="rounded-xl border border-saffron/40 bg-saffron/10 p-4 text-[13px] text-sand-700 leading-relaxed">
      {children}
    </div>
  );
}function BriefingPanel({ data }) {
  const qs = data.qualitaetsstatus || {};
  return (
    <div className="space-y-5" data-testid="briefing-panel">
      {/* Headline + Status-Zeile */}
      <div className="rounded-2xl border border-clay-dark/25 bg-cream-50 p-5 md:p-6 space-y-4">
        <p className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
          Montagsbriefing Pflegequalität
        </p>
        <p className="font-display text-2xl md:text-3xl text-sand-800 font-semibold leading-tight" data-testid="briefing-headline">
          {data.headline}
        </p>
        {data.trend_story && (
          <p className="text-[13px] text-sand-700 italic" data-testid="briefing-trend-story">
            {data.trend_story}
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 pt-1">
          <BriefingStat label="Dokumente geprüft" value={qs.dokumente_geprueft || 0} testid="briefing-stat-docs" />
          <BriefingStat label="QPR-Readiness Ø" value={`${qs.qpr_readiness_avg || 0} %`} testid="briefing-stat-qpr" />
          <BriefingStat label="Auffälligkeiten" value={qs.auffaelligkeiten || 0} testid="briefing-stat-auff" accent="terra" />
          <BriefingStat label="Vor Freigabe korrigiert" value={qs.vor_freigabe_korrigiert || 0} testid="briefing-stat-korr" accent="sage-dark" />
          <BriefingStat label="Noch offen" value={qs.noch_offen || 0} testid="briefing-stat-offen" accent="brand-darkred" />
        </div>
      </div>

      {/* Trend-Vergleich */}
      {data.trend_kpis?.length > 0 && (
        <div className="rounded-xl border border-cream-300 bg-white p-4 space-y-3" data-testid="briefing-trend">
          <div className="flex items-baseline justify-between flex-wrap gap-1">
            <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-sand-600">
              Trend · Aktuell vs. Vorperiode
            </p>
            {data.vergleich && (
              <p className="text-[11px] text-sand-500 italic">
                {data.vergleich.aktuell_zeitraum} ↔ {data.vergleich.vorperiode_zeitraum}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {data.trend_kpis.map((t, i) => (
              <TrendCard key={i} kpi={t} testid={`briefing-trend-${i}`} />
            ))}
          </div>
        </div>
      )}

      {/* Top-Feststellungen + Empfohlene Maßnahmen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-cream-300 bg-white p-4" data-testid="briefing-top-findings">
          <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-sand-600 mb-2.5">
            Top-Feststellungen
          </p>
          {data.top_feststellungen?.length > 0 ? (
            <ul className="space-y-1.5">
              {data.top_feststellungen.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13px] text-sand-800 leading-snug">
                  <span className="inline-flex items-center justify-center min-w-[26px] h-[20px] rounded bg-terra/15 text-terra text-[11.5px] font-bold tabular-nums">
                    {t.anzahl}×
                  </span>
                  <span>{t.titel}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12.5px] text-sand-500 italic">Keine kritischen Feststellungen.</p>
          )}
        </div>

        <div className="rounded-xl border border-sage-dark/25 bg-sage-dark/5 p-4" data-testid="briefing-empfehlungen">
          <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-sage-dark mb-2.5">
            Empfohlene Maßnahmen diese Woche
          </p>
          {data.empfohlene_massnahmen?.length > 0 ? (
            <ol className="space-y-1.5">
              {data.empfohlene_massnahmen.map((m, i) => (
                <li key={i} className="text-[13px] text-sand-800 leading-snug">
                  {i + 1}. {m}
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-[12.5px] text-sand-500 italic">Keine zusätzlichen Maßnahmen empfohlen.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function TrendCard({ kpi, testid }) {
  const verdictColor =
    kpi.verdict === "good" ? "text-sage-dark"
    : kpi.verdict === "bad" ? "text-brand-darkred"
    : "text-sand-500";
  const arrow =
    kpi.direction === "up" ? "▲"
    : kpi.direction === "down" ? "▼"
    : "=";
  const pctText =
    kpi.delta_pct == null
      ? "neu"
      : `${kpi.delta_pct > 0 ? "+" : ""}${kpi.delta_pct}%`;
  return (
    <div className="rounded-lg bg-cream-50 border border-cream-300 p-3" data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-[0.12em] font-bold text-sand-600 leading-tight">
        {kpi.label}
      </p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-display font-semibold text-sand-800 tabular-nums">
          {typeof kpi.current === "number" ? Math.round(kpi.current * 10) / 10 : kpi.current}{kpi.unit}
        </span>
      </div>
      <p className={`text-[12px] font-bold ${verdictColor} mt-1 flex items-center gap-1`}>
        <span className="text-[14px]">{arrow}</span>
        <span>{pctText}</span>
      </p>
      <p className="text-[10.5px] text-sand-500 mt-0.5">
        Vorperiode: {typeof kpi.previous === "number" ? Math.round(kpi.previous * 10) / 10 : kpi.previous}{kpi.unit}
      </p>
    </div>
  );
}

function BriefingStat({ label, value, testid, accent }) {
  const colorClass = accent === "terra" ? "text-terra"
    : accent === "sage-dark" ? "text-sage-dark"
    : accent === "brand-darkred" ? "text-brand-darkred"
    : "text-sand-800";
  return (
    <div className="rounded-lg bg-white border border-cream-300 px-3 py-2.5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-sand-500 leading-tight">
        {label}
      </p>
      <p className={`mt-1 text-xl font-display font-semibold ${colorClass} tabular-nums leading-none`}>
        {value}
      </p>
    </div>
  );
}

function HeroKpisPanel({ data }) {  const k = data.kpis || {};
  const beob = k.beobachtung_vs_diagnose || { total: 0, fall_a_beobachtung_zu_diagnose: 0, fall_b_x_bei_y: 0 };
  const xdoc = k.cross_doc_widersprueche || { total: 0, rot: 0, gelb: 0 };
  const qi = data.qualitaetsindex || 0;

  return (
    <div className="space-y-6" data-testid="hero-kpis-panel">
      {/* Hero-Banner */}
      <div className="rounded-2xl bg-gradient-to-br from-terra/12 via-saffron/8 to-cream-100 border border-cream-300 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-1.5">
            Pflege-KI · MD-Story dieses Zeitraums
          </p>
          <h3 className="font-display text-3xl md:text-4xl text-sand-800 font-semibold tracking-tight">
            Die KI hat <span data-testid="hero-kpi-verhindert-total" className="text-terra">
              {data.verhinderte_halluz_total || 0}
            </span>{" "}
            fachliche Fehler erkannt,
            <br className="hidden md:block" />
            bevor sie dokumentiert wurden.
          </h3>
          <p className="mt-3 text-[13.5px] text-sand-600 leading-relaxed max-w-2xl">
            {data.label}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 shrink-0">
          <MiniStat
            icon={Target}
            label="Qualitätsindex"
            value={`${qi} %`}
            testid="hero-kpi-qualitaetsindex"
            accent="sage-dark"
          />
          <MiniStat
            icon={Clock}
            label="Zeitersparnis"
            value={`${data.zeitersparnis_minuten || 0} Min`}
            testid="hero-kpi-zeitersparnis"
            accent="terra"
          />
        </div>
      </div>

      {/* Aufschlüsselung pro Regel */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          icon={Award}
          title="QPR-Readiness Ø"
          value={(k.qpr_readiness?.avg_score || 0) + "/100"}
          subline={`${k.qpr_readiness?.checks_total || 0} Checks · ${k.qpr_readiness?.ready_pct || 0} % MD-ready`}
          testid="hero-kpi-qpr"
        />
        <KpiCard
          icon={Shield}
          title="Wertungen ohne Datenbasis"
          value={k.wertung_ohne_datenbasis || 0}
          subline="Defensiv-Interpretation in NBA-Modulen verhindert"
          testid="hero-kpi-wertung"
        />
        <KpiCard
          icon={ShieldAlert}
          title="Beobachtung → Diagnose"
          value={beob.total || 0}
          subline={`Fall A: ${beob.fall_a_beobachtung_zu_diagnose || 0} · Fall B (X bei Y): ${beob.fall_b_x_bei_y || 0}`}
          testid="hero-kpi-beobachtung"
        />
        <KpiCard
          icon={ListChecks}
          title="Cross-Doc-Widersprüche"
          value={xdoc.total || 0}
          subline={`Rot: ${xdoc.rot || 0} · Gelb: ${xdoc.gelb || 0}`}
          testid="hero-kpi-crossdoc"
        />
        <KpiCard
          icon={AlertTriangle}
          title="NBA-Konsistenz-Blocks"
          value={k.nba_konsistenz_blocks || 0}
          subline="Unbegründete PG-Höherstufungen abgefangen"
          testid="hero-kpi-nbablocks"
        />
      </div>

      <p className="text-[11.5px] text-sand-500 italic leading-relaxed">
        Basis: {data.generierungen_geprueft || 0} geprüfte Generierungen ·
        Zeitraum {data.zeitraum_tage} Tage · Zeitersparnis-Heuristik: 4 Minuten je verhinderter Halluzination
        (manuelle Nachkorrektur durch PFK).
      </p>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, testid, accent = "terra" }) {
  return (
    <div className="rounded-xl bg-white border border-cream-300 p-3 min-w-[130px]" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 text-${accent}`} strokeWidth={2} />
        <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-sand-600">
          {label}
        </span>
      </div>
      <div className="mt-1 text-2xl font-display font-semibold text-sand-800">{value}</div>
    </div>
  );
}function KpiCard({ icon: Icon, title, value, subline, testid }) {
  return (
    <div className="rounded-xl border border-cream-300 bg-white p-4 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-clay-dark" strokeWidth={2} />
        <h4 className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-sand-700">
          {title}
        </h4>
      </div>
      <div className="text-3xl font-display font-semibold text-sand-800 leading-none">
        {value}
      </div>
      <p className="text-[11.5px] text-sand-500 leading-snug">{subline}</p>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="space-y-8" data-testid="dashboard-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-2xl border border-cream-300 bg-white p-6">
          <div className="h-6 w-1/3 bg-cream-200 rounded animate-pulse mb-4" />
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[1,2,3,4].map((j) => (
              <div key={j} className="h-20 bg-cream-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
